'use client'

import { useMemo, useState } from 'react'
import { useSearchParam } from 'react-use'

import { gql } from '../../__generated__/graphql/catalog/gql'
import { CartContainer } from '../../brunswick/containers/cart'
import { reportClientError } from '../_components/chrome/scripts/DataDogRumScript'

import { useNextQuery } from './useNextQuery'

export const getRecoveredCartItemsQuery = gql(`
  query GetRecoveredCartItems($skus: [String!]) {
    productCatalogVariants(skus: $skus) {
      __typename
      id
      sku
      shopifyId
      shopifyProductId
      availability
      price {
        amount
        currencyCode
      }
      compareAtPrice {
        amount
      }
      group {
        __typename
        id
        primaryCategory {
          key
          analyticsId
          displayValue
        }
        finalSale
        siteWidePromoEligible
        product {
          __typename
          id
          handle
        }
      }
    }
  }
`)

export const useCartRecovery = () => {
  const queryKey = 'cart'
  const initialQueryValue = useSearchParam(queryKey)
  const { addItems, cart, status: cartStatus } = CartContainer.useContainer()

  // The initial comma-separated string of SKU's from the cart recovery URL parameter
  // This will be set to null once all items in the query are recovered
  const [recoveryQuery, setRecoveryQuery] = useState<string | null>(initialQueryValue)

  // Get applicable skus that are present in the recovery query
  const recoveredSkus = useMemo(() => {
    if (recoveryQuery && recoveryQuery.length > 0 && cartStatus !== 'pending') {
      const skus = (recoveryQuery as string).split(',')
      // Filter out skus that are already in the cart
      return skus.filter(sku => {
        const alreadyExists = Object.values(cart.items).some(item => item.sku === sku)
        return !alreadyExists
      })
    }
    return []
  }, [recoveryQuery, cartStatus, cart.items])

  // Fetch variants for pending skus
  useNextQuery(getRecoveredCartItemsQuery, {
    variables: { skus: recoveredSkus },
    skip: recoveredSkus.length === 0,
    ssr: false,
    onCompleted: data => {
      // create local cart item objects from response, excluding embroidery
      const recoveredCartItems: Parameters<typeof addItems>[0] = (data.productCatalogVariants ?? [])
        .filter(recoveredVariant => recoveredVariant.group.primaryCategory.key !== 'EMBROIDERY')
        .map(recoveredVariant => {
          if (!recoveredVariant.sku) return null
          return {
            finalSale: recoveredVariant.group.finalSale,
            item: {
              ...recoveredVariant,
              product: recoveredVariant.group.product,
              variantGroup: recoveredVariant.group,
            },
            properties: {},
            quantity: 1,
            swPromoEligible: recoveredVariant.group.siteWidePromoEligible,
          }
        })
        .filter((ci): ci is NonNullable<typeof ci> => !!ci)

      if (recoveredCartItems.length > 0) {
        addItems(recoveredCartItems)
      }
      setRecoveryQuery(null)
    },
    onError: error => {
      reportClientError({
        error,
        context: {
          scope: 'Cart Recovery Error',
        },
      })
    },
  })

  return null
}

export const CartRecovery = () => {
  useCartRecovery()
  return null
}
